import React, { useState, useEffect, useCallback } from 'react'
import { Button, Card, Modal } from 'react-daisyui'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import DefaultLogo from "../../assets/default-logo.png"
import { useNavigate } from 'react-router-dom'
import { AiOutlineShop, AiOutlineCalendar, AiOutlineEnvironment, AiOutlineLeft, AiOutlineRight, AiOutlineEye, AiOutlineTag, AiOutlineInfoCircle } from "react-icons/ai"
import { BsShop } from "react-icons/bs"

function Merchant() {
    const [merchants, setMerchants] = useState([])
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [selectedMerchant, setSelectedMerchant] = useState(null)
    const [showDetailModal, setShowDetailModal] = useState(false)

    const navigate = useNavigate()
    const loadMerchants = useCallback(async () => {
        setLoading(true)
        const params = {
            access: 'auth',
            method: API.MERCHANT.type,
            url: API.MERCHANT.url,
            reqBody: {
                page: page,
                rows: 10
            }
        }
        try {
            const response = await Api(params)
            if (response?.status) {
                setMerchants(response.data.data)
            setLastPage(response.data.lastPage)
            setPage(response.data.page)
        }
        } catch (error) {
            console.error('Error loading merchants:', error)
        } finally {
            setLoading(false)
    }
    }, [page])

    useEffect(() => {
        loadMerchants()
    }, [loadMerchants])

    const handlePrev = () => {
        setPage(page > 1 ? page - 1 : 1)
    }

    const handleNext = () => {
        setPage(page < lastPage ? page + 1 : lastPage)
    }

    // Show merchant details modal
    const showMerchantDetails = (merchant) => {
        setSelectedMerchant(merchant)
        setShowDetailModal(true)
    }

    const handleCloseModal = () => {
        setShowDetailModal(false)
        setSelectedMerchant(null)
    }

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-4">
            <div className="bg-gray-200 h-8 rounded w-1/3 mx-auto"></div>
            <div className="grid grid-cols-1 gap-4">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
                ))}
            </div>
        </div>
    )

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="mx-auto max-w-sm px-5 py-6">
                    <LoadingSkeleton />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="mx-auto max-w-sm px-5 py-6">
                <div className='flex flex-col space-y-6 scrollbar-hide'>

                    {/* Header Section */}
                    <div className="text-center space-y-2">
                        <div className="flex items-center justify-center space-x-2">
                            <BsShop className="text-2xl text-blue-600" />
                            <h1 className="text-2xl font-bold text-gray-800">Penyelenggara Voucher</h1>
                        </div>
                        <p className="text-gray-600 text-sm">Temukan merchant partner kami</p>
                        {merchants.length > 0 && (
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg border border-blue-100">
                                <div className="flex items-center justify-center space-x-2">
                                    <AiOutlineShop className="text-blue-600" />
                                    <span className="text-sm font-medium text-gray-700">
                                        {merchants.length} Merchant{merchants.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Merchant Grid */}
                    {merchants.length === 0 ? (
                        <Card className="bg-gradient-to-r from-gray-50 to-white border-2 border-dashed border-gray-300">
                            <Card.Body className="text-center py-12">
                                <BsShop className="text-6xl text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium text-lg">Belum ada merchant</p>
                                <p className="text-gray-400 text-sm mt-1">Merchant akan segera hadir</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            {merchants.map((merchant, i) => (
                                <Card
                                    key={i}
                                    className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 border-0 overflow-hidden cursor-pointer"
                                    onClick={() => navigate(`/merchant/${merchant.partner_id}`)}
                                >
                                    <Card.Body className="p-0">
                                        <div className="p-6">
                                            <div className="flex items-start space-x-4">
                                                {/* Merchant Logo */}
                                                <div className="flex-shrink-0">
                                                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center overflow-hidden border border-gray-200">
                                                        <img
                                                            src={merchant?.logo || DefaultLogo}
                                                            alt={merchant?.name || 'Merchant'}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.target.src = DefaultLogo
                                                            }}
                                                        />
                                                    </div>
                                    </div>
                                    
                                                {/* Merchant Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div>
                                                            <h3 className="text-lg font-bold text-gray-800 truncate">
                                                                {merchant?.name || 'Merchant Name'}
                                                            </h3>
                                                            <p className="text-sm text-gray-500 mt-1">
                                                                Partner ID: {merchant?.partner_id}
                                                            </p>
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                showMerchantDetails(merchant)
                                                            }}
                                                        >
                                                            <AiOutlineEye className="mr-1" />
                                                            Detail
                                                        </Button>
                                </div>
                                
                                                    {/* Merchant Stats */}
                                                    <div className="grid grid-cols-2 gap-3 mt-4">
                                                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg">
                                                            <div className="flex items-center space-x-2">
                                                                <AiOutlineTag className="text-blue-600" />
                                                                <div>
                                                                    <p className="text-xs text-blue-700 font-medium">Voucher</p>
                                                                    <p className="text-sm font-bold text-blue-800">
                                                                        {merchant?.voucher_count || 0}
                                                                    </p>
                                                                </div>
                                                            </div>
                        </div>
                       
                                                        <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-lg">
                                                            <div className="flex items-center space-x-2">
                                                                <AiOutlineEnvironment className="text-green-600" />
                                                                <div>
                                                                    <p className="text-xs text-green-700 font-medium">Lokasi</p>
                                                                    <p className="text-sm font-bold text-green-800">
                                                                        {merchant?.redeem_merchants?.length || 0}
                                                                    </p>
                    </div>
                    </div>
                </div>
            </div>

                                                    {/* Description */}
                                                    {merchant?.description && (
                                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                                            <p className="text-sm text-gray-600 line-clamp-2">
                                                                {merchant.description}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {merchants.length > 0 && (
                        <div className="flex justify-center items-center space-x-4 pt-4">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page === 1}
                                onClick={handlePrev}
                                className="border-gray-300"
                            >
                                <AiOutlineLeft className="mr-1" />
                                Previous
                            </Button>

                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">Page</span>
                                <span className="bg-blue-500 text-white px-3 py-1 rounded-lg font-semibold">
                                    {page}
                                </span>
                                <span className="text-sm text-gray-600">of {lastPage}</span>
                            </div>

                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page === lastPage}
                                onClick={handleNext}
                                className="border-gray-300"
                            >
                                Next
                                <AiOutlineRight className="ml-1" />
                            </Button>
                        </div>
                    )}
                </div>
                        </div>

            {/* Merchant Detail Modal */}
            <Modal open={showDetailModal} onClickBackdrop={handleCloseModal} className="max-w-md mx-4">
                <Modal.Header className="flex items-center space-x-2">
                    <BsShop className="text-blue-600" />
                    <span className="font-bold">Detail Merchant</span>
                </Modal.Header>
                <Modal.Body>
                    {selectedMerchant && (
                        <div className="space-y-4">
                            {/* Merchant Logo */}
                            <div className="flex justify-center">
                                <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center overflow-hidden border border-gray-200">
                                    <img
                                        src={selectedMerchant?.logo || DefaultLogo}
                                        alt={selectedMerchant?.name || 'Merchant'}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.src = DefaultLogo
                                        }}
                                    />
                    </div>
                        </div>

                            {/* Merchant Info */}
                            <div className="text-center space-y-2">
                                <h3 className="text-lg font-bold text-gray-800">
                                    {selectedMerchant?.name || 'Merchant Name'}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    Partner ID: {selectedMerchant?.partner_id}
                                </p>
                        </div>

                            {/* Stats */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                    <AiOutlineTag className="text-blue-600 mx-auto mb-1" />
                                    <p className="text-xs text-blue-700 font-medium">Total Voucher</p>
                                    <p className="text-lg font-bold text-blue-800">
                                        {selectedMerchant?.voucher_count || 0}
                                    </p>
                        </div>
                                <div className="bg-green-50 p-3 rounded-lg text-center">
                                    <AiOutlineEnvironment className="text-green-600 mx-auto mb-1" />
                                    <p className="text-xs text-green-700 font-medium">Lokasi</p>
                                    <p className="text-lg font-bold text-green-800">
                                        {selectedMerchant?.redeem_merchants?.length || 0}
                                    </p>
                        </div>
                        </div>

                            {/* Description */}
                            {selectedMerchant?.description && (
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-sm text-gray-700">
                                        {selectedMerchant.description}
                                    </p>
                        </div>
                            )}

                            {/* Redeem Locations */}
                            {selectedMerchant?.redeem_merchants && selectedMerchant.redeem_merchants.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-gray-800 flex items-center space-x-2">
                                        <AiOutlineEnvironment className="text-green-600" />
                                        <span>Lokasi Penukaran</span>
                                    </h4>
                                    {selectedMerchant.redeem_merchants.map((location, i) => (
                                        <div key={i} className="bg-white border border-gray-200 p-3 rounded-lg">
                                            <p className="font-medium text-gray-800">{location.name}</p>
                                            <p className="text-sm text-gray-600 mt-1">{location.address}</p>
                                </div>
                            ))}
                        </div>
                            )}
                    </div>
                    )}
                </Modal.Body>
                <Modal.Actions className="flex justify-end space-x-2">
                    <Button onClick={handleCloseModal} variant="outline">
                        Tutup
                    </Button>
                    <Button
                        onClick={() => {
                            handleCloseModal()
                            navigate(`/merchant/${selectedMerchant?.partner_id}`)
                        }}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        Lihat Voucher
                    </Button>
                </Modal.Actions>
            </Modal>





        </div>
    )
}

export default Merchant